import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ApiClientService, CachedValuesService, ToastService, RightPanelService } from '@services/index';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take } from 'rxjs';
import { AddUserToTenantByEmailForm } from './add-user-to-tenant-modal.model';
import { Role } from 'app/enums';
import { CustomFormModule } from '@shared/custom-form/custom-form.module';
import { SharedModule } from '@shared/shared.module';

@UntilDestroy()
@Component({
    selector: 'app-add-user-to-tenant-modal',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, CustomFormModule, SharedModule],
    templateUrl: './add-user-to-tenant-modal.component.html',
    styleUrl: './add-user-to-tenant-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddUserToTenantModalComponent implements OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly translateService = inject(TranslateService);
    private readonly toastService = inject(ToastService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly rightPanelService = inject(RightPanelService);

    public showPotentialValidationMessages = false;
    public loading = false;
    public form: FormGroup<AddUserToTenantByEmailForm>;

    public get f() {
        return this.form.controls;
    }

    public ngOnInit() {
        const fb = new FormBuilder();
        this.form = fb.nonNullable.group({
            email: ['', [Validators.required, Validators.email]],
            roles: [
                [] as Role[],
                [
                    (control: AbstractControl) =>
                        Array.isArray(control.value) && control.value.length > 0 ? null : { required: true }
                ]
            ]
        });
    }

    public isDisabled() {
        return this.loading || !this.form.dirty;
    }

    public cancel() {
        this.rightPanelService.hide();
    }

    public save() {
        this.showPotentialValidationMessages = true;
        this.changeDetectorRef.markForCheck();

        if (this.form.valid) {
            this.loading = true;
            this.changeDetectorRef.markForCheck();

            this.apiClientService
                .addUserToTenantByEmail$({
                    email: this.f.email.value,
                    roles: this.f.roles.value
                })
                .pipe(untilDestroyed(this), take(1))
                .subscribe({
                    next: () => {
                        const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                        const message = this.translateService.instant(
                            'FEATURES.USER.TOAST_MESSAGES.ADD_USER_TO_TENANT_SUCCESS'
                        ) as string;
                        this.cachedValuesService.invalidateCache();
                        this.toastService.open(title, message, { type: 'success' });
                        this.rightPanelService.hide();
                    },
                    error: (err: HttpErrorResponse) => {
                        this.loading = false;
                        this.changeDetectorRef.markForCheck();
                        const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                        const apiMessage = (err.error as { message?: string })?.message;
                        const message =
                            apiMessage ||
                            (this.translateService.instant(
                                'FEATURES.USER.TOAST_MESSAGES.ADD_USER_TO_TENANT_ERROR'
                            ) as string);
                        this.toastService.open(title, message, { type: 'error', progressBar: true });
                    }
                });
        }
    }
}
